import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, ROLE_LABEL, type PublicUser } from '../../api/client';

export default function Usuarios() {
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState<string | null>(null);
  const [modal, setModal] = useState(false);

  const load = useCallback(async () => {
    const { users } = await api.get<{ users: PublicUser[] }>('/users');
    setUsers(users);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function onCreate(form: FormData) {
    setBusy(true);
    try {
      await api.post('/users', {
        email: form.get('email'),
        password: form.get('password'),
        fullName: form.get('fullName'),
        role: form.get('role'),
      });
      toast.success('Usuário criado.');
      await load();
      return true;
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Falha ao criar usuário';
      toast.error(msg);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onPatch(id: string, body: object) {
    try {
      const r = await api.patch<{ user: PublicUser; tempPassword?: string }>(`/users/${id}`, body);
      if (r.tempPassword) setTemp(r.tempPassword);
      else toast.success('Usuário atualizado.');
      await load();
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Falha na atualização';
      toast.error(msg);
    }
  }

  return (
    <div>
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Usuários</p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">Time</h1>
        </div>
        <button
          onClick={() => setModal(true)}
          className="inline-flex h-9 items-center rounded-lg bg-[var(--accent)] px-4 text-sm font-semibold text-white hover:bg-[#c40a20] transition-colors"
        >
          Novo usuário
        </button>
      </header>
      {temp && (
        <div className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <p className="font-medium">Senha temporária gerada (mostre ao usuário, some ao recarregar):</p>
          <code className="mt-1 block rounded bg-[var(--bg-subtle)] px-2 py-1">{temp}</code>
          <button onClick={() => setTemp(null)} className="mt-2 text-xs text-[var(--text-dim)] hover:text-[var(--text)]">Fechar</button>
        </div>
      )}
      <div className="mt-4 rounded-lg border border-[var(--border)] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left text-xs text-[var(--text-dim)] uppercase tracking-wider">
              <th className="px-4 py-3 font-medium">Nome</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Papel</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--border)] last:border-0">
                <td className="px-4 py-3 font-medium">{u.fullName}</td>
                <td className="px-4 py-3 text-[var(--text-dim)]">{u.email}</td>
                <td className="px-4 py-3">{ROLE_LABEL[u.role]}</td>
                <td className="px-4 py-3">{u.isActive ? 'Ativo' : 'Inativo'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <select
                      value={u.role}
                      onChange={(e) => onPatch(u.id, { role: e.target.value })}
                      className="h-8 rounded border border-[var(--border-strong)] bg-white px-1 text-xs"
                    >
                      <option value="gestor">Gestor</option>
                      <option value="vendedor">Vendedor</option>
                    </select>
                    <button
                      onClick={() => onPatch(u.id, { isActive: !u.isActive })}
                      className="h-8 rounded border border-[var(--border-strong)] px-2 text-xs hover:bg-[var(--bg-subtle)]"
                    >
                      {u.isActive ? 'Desativar' : 'Ativar'}
                    </button>
                    <button
                      onClick={() => onPatch(u.id, { newPassword: genSenhaTemp() })}
                      className="h-8 rounded border border-[var(--border-strong)] px-2 text-xs hover:bg-[var(--bg-subtle)]"
                    >
                      Trocar senha
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <UsuarioModal busy={busy} onClose={() => setModal(false)} onCreate={onCreate} />}
    </div>
  );
}

function UsuarioModal({
  busy, onClose, onCreate,
}: {
  busy: boolean; onClose: () => void; onCreate: (form: FormData) => Promise<boolean>;
}) {
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (await onCreate(new FormData(e.target as HTMLFormElement))) onClose();
        }}
        className="w-full max-w-sm rounded-xl border border-[var(--border)] bg-white p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold tracking-tight">Novo usu&aacute;rio</h2>
        <div className="mt-4 space-y-3 text-sm">
          <input name="fullName" placeholder="Nome completo" required className="w-full h-10 rounded-lg border border-[var(--border-strong)] px-3" />
          <input name="email" type="email" placeholder="email@empresa.com" required className="w-full h-10 rounded-lg border border-[var(--border-strong)] px-3" />
          <input name="password" type="password" placeholder="Senha (min. 8)" required minLength={8} className="w-full h-10 rounded-lg border border-[var(--border-strong)] px-3" />
          <select name="role" className="w-full h-10 rounded-lg border border-[var(--border-strong)] px-3" defaultValue="vendedor">
            <option value="gestor">Gestor de vendas</option>
            <option value="vendedor">Vendedor</option>
          </select>
        </div>
        <div className="mt-5 flex justify-end gap-2 text-sm">
          <button type="button" onClick={onClose} className="h-9 rounded-lg border border-[var(--border-strong)] px-3 font-medium hover:bg-[var(--bg-subtle)]">Cancelar</button>
          <button type="submit" disabled={busy} className="h-9 rounded-lg bg-[var(--accent)] px-3 font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Criar</button>
        </div>
      </form>
    </div>
  );
}

function genSenhaTemp(): string {
  const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return `${out}!1Aa`;
}
